import { AfterViewChecked, Component, inject, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { Moment } from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, combineLatest, EMPTY, firstValueFrom, Observable, of, Subject } from 'rxjs';
import { debounceTime, finalize, first, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Permission } from 'src/app/common/enums';
import { Messages } from 'src/app/common/enums/messages';
import { CreateOrUpdateDelegationRequest } from 'src/app/contracts/requests/create-or-update-delegation-request';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { DelegationService } from 'src/app/data/delegation.service';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { WorkerService } from 'src/app/data/worker.service';
import { Country } from 'src/app/models/Country';
import { DictionaryItem } from 'src/app/models/dictionary-item';
import { AdvancePayment } from 'src/app/models/dtos/advance-payment';
import { DelegationDto } from 'src/app/models/dtos/delegation-dto';
import { InternalWorkerAgreementDto } from 'src/app/models/dtos/internal-worker-agreement-dto';
import { CurrencyEnum } from 'src/app/models/enums/currency-enum';
import { DelegationStatusEnum } from 'src/app/models/enums/delegation-status-enum';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { PaymentTypeEnum } from '../../models/enums/payment-type-enum';
import { SendMultipleDelegationsToRejectionRequest } from 'src/app/contracts/requests/send-multiple-delegations-to-rejection-request';
import { SendMultipleDelegationsToApprovalRequest } from 'src/app/contracts/requests/send-multiple-delegations-to-approval-request';

@Component({
  selector: 'app-delegation-form',
  templateUrl: './delegation-form.component.html',
  styleUrls: ['./delegation-form.component.scss'],
  standalone: false
})
export class DelegationFormComponent implements OnInit, AfterViewChecked, OnDestroy {
  data = inject<{
    workerId: number;
    recordId: number;
    authServerUserId: string;
  }>(MAT_DIALOG_DATA);

  private formBuilder = inject(UntypedFormBuilder);
  private dialogRef = inject<MatDialogRef<DelegationFormComponent>>(MatDialogRef);
  private workerService = inject(WorkerService);
  private dictionaryService = inject(DictionaryService);
  private delegationService = inject(DelegationService);
  private spinner = inject(NgxSpinnerService);
  private snackbar = inject(SnackBarService);
  private dialog = inject(MatDialog);
  private translateService = inject(TranslateService);
  private authService = inject(AuthService);

  listOfCurrencies$: Observable<DictionaryItem[]> = this.dictionaryService.getCurrencies();
  listOfCountries$: Observable<DictionaryItem[]> = this.dictionaryService.getCountries();
  listOfCities$: Observable<string[]>;
  delegationPurposes$: Observable<DictionaryItem[]> = this.dictionaryService.getDelegationPurposes();
  transportTypes$: Observable<DictionaryItem[]> = this.dictionaryService.getTransportTypes();

  accesedEmployerObjects$: Observable<DictionaryItem[]>;
  private accesedEmployerObjectSubject = new Subject<DictionaryItem[]>();

  formGroup: UntypedFormGroup;
  fullNameSubject = new BehaviorSubject<string>('');
  private readonly unsubscribe$ = new Subject<void>();
  private readonly timeBetweenInput = 300;
  private readonly minimumInputLetters = 2;
  timepickerTimeFormat: number = 24;
  defaultTime: string = '00:00';

  agreementSubject = new BehaviorSubject<InternalWorkerAgreementDto[]>([]);
  agreements$ = this.agreementSubject.asObservable();
  public dataSource = new MatTableDataSource();
  private loggedWorkerId: number;
  public hasAdvancePayment: boolean = false;
  private pendingForEmployerObjectId: number | null = null;
  public isDataReady = false;


  constructor() {
    this.formGroup = this.buildFormGroup();
  }

  get activeAgreement(): InternalWorkerAgreementDto {
    return this.formGroup.get('activeAgreement').value;
  }

  get advancePayment() {
    return this.formGroup.get('advancePayment') as UntypedFormGroup;
  }

  get destinationCity(): UntypedFormControl {
    return this.formGroup.get('destinationCity') as UntypedFormControl;
  }

  get statusIdControl(): UntypedFormControl {
    return this.formGroup.get('statusId') as UntypedFormControl;
  }

  get statusName(): UntypedFormControl {
    return this.formGroup.get('statusName') as UntypedFormControl;
  }

  get startDateControl(): AbstractControl {
    return this.formGroup.get('startDate');
  }
  get startTimeControl(): AbstractControl {
    return this.formGroup.get('startTime');
  }
  get startDateTimeControl(): AbstractControl {
    return this.formGroup.get('startDateTime');
  }
  get endDateControl(): AbstractControl {
    return this.formGroup.get('endDate');
  }
  get endTimeControl(): AbstractControl {
    return this.formGroup.get('endTime');
  }
  get endDateTimeControl(): AbstractControl {
    return this.formGroup.get('endDateTime');
  }
  get activeAgreementControl(): AbstractControl {
    return this.formGroup.get('activeAgreement');
  }

  get forEmployerObject(): UntypedFormControl {
    return this.formGroup.get('forEmployerObject') as UntypedFormControl;
  }

  get startDate(): Date {
    return this.startDateControl.value;
  }
  get startTime(): string {
    return this.startTimeControl.value;
  }
  get startDateTime(): Date {
    return this.startDateTimeControl.value;
  }
  get endDate(): Date {
    return this.endDateControl.value;
  }
  get endTime(): string {
    return this.endTimeControl.value;
  }
  get endDateTime(): Date {
    return this.endDateTimeControl.value;
  }

  get shouldShowForEmployerObjectControl(): boolean {
    return (
      (this.hasAccountingManagementPermission() || this.authService.hasPermission(Permission.ManageMyDelegations)) &&
      this.activeAgreement &&
      this.activeAgreement.HasInternalEmployerObjects
    );
  }

  async ngOnInit(): Promise<void> {
    this.loggedWorkerId = await firstValueFrom(this.authService.getWorkerIdByAuthServerUserId());
    this.onDestinationCityChange();
    this.onStartDateChanges();
    this.onEndDateChanges();
    this.onActiveAgreementChange();

    this.fetchWorkerFullName(this.data.workerId);
    this.fetchAgremeents(this.data.workerId);
  }

  ngAfterViewChecked(): void {
    if (this.statusIdControl.value && this.statusIdControl.value !== DelegationStatusEnum.Draft) {
      this.formGroup.disable();
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }
  startDateFilter = (date: Moment): boolean =>
    this.formGroup && (!this.endDate || date?.isSameOrBefore(this.endDate)) && this.isDateValidAgainstActiveAgreementData(date);

  endDateFilter = (date: Moment): boolean =>
    this.formGroup && date?.isSameOrAfter(this.startDate) && this.isDateValidAgainstActiveAgreementData(date);

  async sendToApprovalClicked() {
    if (this.formGroup.invalid) {
      return;
    }

    if (this.startTime == this.defaultTime && this.endTime == this.defaultTime) {
      await this.openConfirmDialog().then((result) => {
        if (result) {
          this.sendToApproval();
        }
      });
    } else {
      this.sendToApproval();
    }
  }

  async saveAsDraft() {
    if (this.formGroup.invalid) {
      return;
    }

    if (this.startTime == this.defaultTime && this.endTime == this.defaultTime) {
      await this.openConfirmDialog().then((result) => {
        if (result) {
          this.save();
        }
      });
    } else {
      this.save();
    }
  }

  isDelegationActionButtonVisible = () =>
    this.statusIdControl.value === DelegationStatusEnum.PendingApproval &&
    this.data.authServerUserId !== this.authService.authServerUserId &&
    (this.authService.isSupervisor(this.data.authServerUserId)
      || this.hasManagementPermission()
    );

  close = () => this.dialogRef.close();

  public addNewAdvancePayment(advancePayment: AdvancePayment = { Id: null, Value: null, CurrencyId: null } as AdvancePayment) {
    if (!this.hasAdvancePayment) {
      this.showAdvancePayment(advancePayment);
    }
  }

  public showAdvancePayment(advancePayment: AdvancePayment = { Id: null, Value: null, CurrencyId: null } as AdvancePayment) {
    this.advancePayment.patchValue({
      Id: advancePayment?.Id,
      value: advancePayment?.Value,
      currencyId: advancePayment?.CurrencyId ?? CurrencyEnum.PLN,
    });

    this.advancePayment.get('value').setValidators([Validators.required]);
    this.advancePayment.get('currencyId').setValidators([Validators.required]);
    this.advancePayment.updateValueAndValidity();

    this.hasAdvancePayment = true;
  }

  public deleteAdvancePayment() {
    this.hideAdvancePayment();
  }

  public hideAdvancePayment() {
    this.hasAdvancePayment = false;

    this.advancePayment.get('value').clearValidators();
    this.advancePayment.get('currencyId').clearValidators();
    this.advancePayment.updateValueAndValidity();

    this.advancePayment.reset({
      Id: null,
      value: null,
      currencyId: CurrencyEnum.PLN,
    });
  }

  public displayValue(value: DictionaryItem): string | undefined {
    return value?.Name;
  }

  public isSaveAsDraftButtonVisible(): boolean {
    return (this.isDataReady &&
      (!this.statusIdControl.value || this.statusIdControl.value === DelegationStatusEnum.Draft) &&
      (this.authService.hasPermission(Permission.Supervisor)
        || this.hasManagementPermission()
        || this.loggedWorkerId === this.data.workerId)
    );
  }

  public isSendButtonVisible(): boolean {
    return (this.isDataReady &&
      (!this.statusIdControl.value || this.statusIdControl.value === DelegationStatusEnum.Draft) &&
      (this.authService.hasPermission(Permission.Supervisor)
        || this.hasManagementPermission()
        || this.loggedWorkerId === this.data.workerId)
    );
  }

  public isAdvancePaymentButtonVisible(): boolean {
    return (
      !this.statusIdControl.value ||
      this.statusIdControl.value === DelegationStatusEnum.Draft
    );
  }

  protected newAdvancePayment(advancePayment: AdvancePayment = null): UntypedFormGroup {
    const advancePaymentForm = this.formBuilder.group({
      Id: advancePayment?.Id,
      value: [advancePayment?.Value, [Validators.required]],
      currencyId: [advancePayment?.CurrencyId ?? CurrencyEnum.PLN, Validators.required],
    });

    return advancePaymentForm;
  }

  private async openConfirmDialog(): Promise<boolean> {
    return await firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          data: {
            title: Messages.ConfirmDelegationHoursTitle,
            message: Messages.ConfirmDelegationHoursMessage,
            confirmButtonLabel: this.translateService.instant(Messages.ConfirmDelegationHoursApprove),
            dismissButtonLabel: this.translateService.instant(Messages.ConfirmDelegationHoursCancel),
          },
        })
        .afterClosed(),
    );
  }

  private save() {
    if (this.formGroup.invalid) return;

    this.spinner.show();
    const request = this.createRequest();

    const action$ = !this.data.recordId
      ? this.delegationService.createDelegationRecord(this.data.workerId, request)
      : this.delegationService.updateDelegationRecord(this.data.workerId, this.data.recordId, request);
    const message = !this.data.recordId ? Messages.SuccessfullyCreatedDelegationRecord : Messages.SuccessfullyUpdatedDelegationRecord;

    action$
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((_) => {
        this.snackbar.openSuccessSnackBar(message);
        this.dialogRef.close(true);
      });
  }

  private sendToApproval() {
    if (this.formGroup.invalid) return;

    const request = this.createRequest();
    this.spinner.show();
    const action$ = !this.data.recordId
      ? this.delegationService.createDelegationRecord(this.data.workerId, request)
      : this.delegationService.updateDelegationRecord(this.data.workerId, this.data.recordId, request);

    action$
      .pipe(
        first(),
        switchMap((delegationId) => {
          this.data.recordId = delegationId;

          return this.delegationService.sendToApproval(delegationId).pipe(first());
        }),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((_) => this.closeDialogSuccessfully(Messages.SuccessfullySentToApprovalDelegtion));
  }

  acceptDelegation() {
    this.spinner.show();
    return this.delegationService
      .accept({ DelegationIds: [this.data.recordId] } as SendMultipleDelegationsToApprovalRequest)
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((_) => this.closeDialogSuccessfully(Messages.SuccessfullyAcceptDelegtion));
  }

  rejectDelegation() {
    this.spinner.show();
    return this.delegationService
      .reject({ DelegationIds: [this.data.recordId] } as SendMultipleDelegationsToRejectionRequest)
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((_) => this.closeDialogSuccessfully(Messages.SuccessfullyRejectDelegtion));
  }

  private closeDialogSuccessfully = (message: string) => {
    this.snackbar.openSuccessSnackBar(message);
    this.dialogRef.close(true);
  };

  private buildFormGroup() {
    return this.formBuilder.group(
      {
        startDate: [null, [Validators.required]],
        endDate: [null, [Validators.required]],
        activeAgreement: [null, Validators.required],
        country: [{ value: Country.Poland, disabled: true }, [Validators.required]],
        destinationCity: [null, [Validators.required]],
        delegationPurpose: [null, [Validators.required]],
        transportType: [null, [Validators.required]],
        advancePayment: this.formBuilder.group({
          Id: null,
          value: null,
          currencyId: CurrencyEnum.PLN,
        }),
        statusId: [null],
        statusName: [null],
        startTime: [null, Validators.required],
        endTime: [null, Validators.required],
        startDateTime: [null],
        endDateTime: [null],
        forEmployerObject: [null],
      },
      { validators: this.dateValidator },
    );
  }

  private dateValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const start = control.get('startDateTime');
    const end = control.get('endDateTime');

    return start.value !== null && end.value !== null && start.value < end.value ? null : { dateValid: true };
  };

  private onStartDateChanges() {
    const dateChanges$ = this.startDateControl.valueChanges.pipe(startWith(this.startDateControl.value));
    const timeChanges$ = this.startTimeControl.valueChanges.pipe(startWith(this.startTimeControl.value));

    combineLatest([dateChanges$, timeChanges$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([selectedDate, selectedTime]) => {
        if (selectedDate && !selectedTime) {
          this.startTimeControl.setValue(this.defaultTime, { emitEvent: false });
          selectedTime = this.defaultTime;
        }

        if (this.startDate && this.startTime) {
          const dateTime = this.addTimeToDate(this.startDate, this.startTime);
          this.formGroup.get('startDateTime').setValue(dateTime, { emitEvent: true });
        }
      });
  }

  private onEndDateChanges() {
    const dateChanges$ = this.endDateControl.valueChanges.pipe(startWith(this.endDateControl.value));
    const timeChanges$ = this.endTimeControl.valueChanges.pipe(startWith(this.endTimeControl.value));

    combineLatest([dateChanges$, timeChanges$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([selectedDate, selectedTime]) => {
        if (selectedDate && !selectedTime) {
          this.endTimeControl.setValue(this.defaultTime, { emitEvent: false });
          selectedTime = this.defaultTime;
        }

        if (this.endDate && this.endTime) {
          const dateTime = this.addTimeToDate(this.endDate, this.endTime);
          this.formGroup.get('endDateTime').setValue(dateTime, { emitEvent: false });
        }
      });
  }

  private addTimeToDate = (date: Date, time: string) => new Date(new Date(date).getTime()).setStringTime(time);

  private isDateValidAgainstActiveAgreementData(d: Moment) {
    return (
      this.activeAgreement &&
      (this.activeAgreement.EmploymentDateTo ? d.isSameOrBefore(this.activeAgreement.EmploymentDateTo) : true) &&
      d.isSameOrAfter(this.activeAgreement.EmploymentDateFrom)
    );
  }

  private fetchWorkerFullName(workerId) {
    this.workerService
      .getWorkerFullName(workerId)
      .pipe(first())
      .subscribe((fullname) => {
        return this.fullNameSubject.next(fullname);
      });
  }

  private fetchAgremeents(workerId) {
    const getOnlyOneAgreement = (agreements: InternalWorkerAgreementDto[]) => {
      const activeAgreements = agreements.filter((a) => a.IsActive);

      return activeAgreements.length === 1 && activeAgreements[0]
        || agreements.length === 1 && agreements[0]
        || null;
    };

    this.spinner.show();
    this.agreements$ = this.workerService.getInternalWorkerAgreements(workerId).pipe(
      finalize(() => this.spinner.hide()),
      tap((agreements) => {
        this.agreementSubject.next(agreements);
        const agreement = getOnlyOneAgreement(agreements);

        if (agreement) {
          this.activeAgreementControl.setValue(agreement, { emitEvent: true });
        }
      }),
      tap((agreements) => {
        if (!!this.data.recordId) {
          setTimeout(() => this.fetchDelegationRecord(this.data.recordId, agreements));
        } else {
          this.patchFormGroupValues({ WorkerAgreementId: getOnlyOneAgreement(agreements)?.Id } as DelegationDto, agreements);
        }
      }),
    );
  }

  private fetchDelegationRecord(recordId: number, agreements: InternalWorkerAgreementDto[]) {
    this.spinner.show();
    this.delegationService
      .getDelegationById(this.data.workerId, recordId)
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((record) => {
        this.patchFormGroupValues(record, agreements);
      });
  }

  private patchFormGroupValues(record: DelegationDto, agreements: InternalWorkerAgreementDto[]): void {
    this.formGroup.get('activeAgreement').setValue(
      agreements.find((a) => a.Id === record.WorkerAgreementId),
      { emitEvent: true },
    );

    this.formGroup.patchValue({
      countryId: record.CountryId,
      statusId: record.StatusId,
      statusName: record.StatusName,
      transportType: record.TransportTypeId,
      delegationPurpose: record.DelegationPurposeId,
      paymentTypeId: record.PaymentTypeId,
      destinationCity: record.CityName,
    });

    if (record.AdvancePayment) {
      this.addNewAdvancePayment({
        Id: record.AdvancePayment.Id,
        CurrencyId: record.AdvancePayment.CurrencyId,
        Value: record.AdvancePayment.Value,
      } as AdvancePayment);
    }

    if (record.StartDate && record.EndDate) {
      this.formGroup.patchValue({
        startDateTime: new Date(record.StartDate),
        endDateTime: new Date(record.EndDate),
        startDate: new Date(record.StartDate).resetTime(),
        startTime: record.StartDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
        endDate: new Date(record.EndDate).resetTime(),
        endTime: record.EndDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
      });
    }

    if (record.ForEmployerObjectId) {
      this.pendingForEmployerObjectId = record.ForEmployerObjectId;
    }

    this.formGroup.updateValueAndValidity();
    this.isDataReady = true;
  }

  private createRequest(): CreateOrUpdateDelegationRequest {
    const values = this.formGroup.getRawValue();
    let advancePayment = null;

    if (this.hasAdvancePayment) {
      const advancePaymentValue = values.advancePayment;
      advancePayment = {
        Id: advancePaymentValue.Id,
        Value: advancePaymentValue.value,
        CurrencyId: advancePaymentValue.currencyId,
      } as AdvancePayment;
    }

    return {
      StartDate: values.startDateTime,
      EndDate: values.endDateTime,
      WorkerAgreementId: values.activeAgreement.Id,
      CountryId: values.country,
      TransportTypeId: values.transportType,
      PurposeId: values.delegationPurpose,
      PaymentTypeId: PaymentTypeEnum.TransferEmployeeAccount,
      CityName: values.destinationCity,
      AdvancePayment: advancePayment,
      ForEmployerObjectId: values.forEmployerObject?.Id,
    };
  }

  private onDestinationCityChange() {
    this.listOfCities$ = this.destinationCity.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      debounceTime(this.timeBetweenInput),
      switchMap((value: string) => {
        if (value && value.length > this.minimumInputLetters) {
          return this.dictionaryService.getAllCitiesNames(value);
        } else {
          return of(null);
        }
      }),
    );
  }

  private fetchAccesedEmployerObjects(workerAgreementId: number): void {
    this.spinner.show();
    this.accesedEmployerObjects$ = this.delegationService.getAccesedEmployerObjectsForDelegation(workerAgreementId).pipe(
      finalize(() => this.spinner.hide()),
      tap((eos) => {
        this.accesedEmployerObjectSubject.next(eos);

        if (this.pendingForEmployerObjectId) {
          const selectedEmployerObject = eos.find((emp) => emp.Id === this.pendingForEmployerObjectId);
          if (selectedEmployerObject) {
            this.forEmployerObject.setValue(selectedEmployerObject, { emitEvent: true });
          }
          this.pendingForEmployerObjectId = null;
        }
      }),
    );
  }

  private onActiveAgreementChange() {
    this.activeAgreementControl.valueChanges.subscribe((workerAgreement: InternalWorkerAgreementDto) => {
      if (this.formGroup.disabled) {
        return;
      }

      if (workerAgreement) {
        this.forEmployerObject.setValue(null, { emitEvent: false });

        const hasPermission = this.hasAccountingManagementPermission()
          || this.authService.hasPermission(Permission.ManageMyDelegations);

        const shouldShowEmployerObjects = hasPermission && workerAgreement.HasInternalEmployerObjects;

        if (shouldShowEmployerObjects) {
          this.forEmployerObject.setValidators([Validators.required]);
          this.fetchAccesedEmployerObjects(workerAgreement.Id);
        } else {
          this.forEmployerObject.clearValidators();
          this.accesedEmployerObjects$ = of([]);
          this.accesedEmployerObjectSubject.next([]);
        }

        this.forEmployerObject.updateValueAndValidity();
      }
    });
  }

  private hasManagementPermission() {
    return this.authService.hasAnyPermission([Permission.HRDelegationManagement, Permission.FullDelegationsManagement]);
  }

  private hasAccountingManagementPermission() {
    return this.authService.hasAnyPermission([Permission.AccountingDelegationManagement, Permission.AccountingDelegationManagementAllAccess, Permission.FullDelegationsManagement]);
  }
}
